import {useEffect, useMemo, useState} from 'react';
import {Button, Dialog, DialogContent, DialogTitle, FormGroup, TextField,} from "@mui/material";
import {getBookingDeclineReasons} from "../../request/booking-approvals-requests";
import DeclineBookingReasonSelect from './DeclineBookingReasonSelect';
import useGetRequestBody from '../../../../hooks/useGetRequestBody';
import {SupplierBookingDisableModalButtons, SupplierBookingDisableModalContent} from '../../styles';
import {useSnackbar} from "../../../../context/snackbar-context";

export interface Reason {
    key: string;
    value: string;
}

interface SubmitReason {
    reason: string;
    notes: string
}

interface ModalProps {
    onSubmit: (reason: SubmitReason) => void;
    onClose: () => void;
    booking: {id: string, reasonDeclined: string, reasonDeclinedNote: string, orderNumber: string},
    open: boolean;
}

const SupplierDeclineBookingModal = (props: ModalProps) => {
    const {open, onSubmit, onClose, booking} = props;

    const { snackbarShowMessage } = useSnackbar();

    const declineBookingReasons = useGetRequestBody(getBookingDeclineReasons, useMemo(() => [], []), snackbarShowMessage);
    const otherReason = declineBookingReasons.find((item:Reason) => item.key === 'OTHER');

    const placeHolderText = useMemo(() => ({required: 'Please provide further explanation. (Required)', optional: 'Notes'}), [])

    const [reason, setReason] = useState<Reason | undefined>();
    const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
    const [notesPlaceholder, setNotesPlaceholder] = useState<string>(placeHolderText.optional);
    const [notes, setNotes] = useState<string>('');

    const handleSelectReason = (event:any) => {
        const {target} = event;
        const newReason = declineBookingReasons.find((item: Reason) => item.key === target.value)
        setReason(newReason)
    };

    const handleChangeNotes = (event: any) => {
        const {target} = event;
        setNotes(target.value)
    }

    const handleSubmit = () => {
        if (reason) {
            const reasonDeclined: SubmitReason = {reason: reason.key, notes: notes}
            onSubmit(reasonDeclined)
        } else {
            snackbarShowMessage('Please select a reason', 'error', 1000)
        }
    }

    const handleClose = () => {
        setReason(undefined);
        setNotes('');
        setDisableSubmit(true)
        onClose();
    }

    useEffect(() => {
        setDisableSubmit(!reason || (reason === otherReason && !notes));
    }, [reason, otherReason, notes, setDisableSubmit]);

    useEffect(() => {
        if(reason === otherReason) {
            setNotesPlaceholder(placeHolderText.required)
        } else {
            setNotesPlaceholder(placeHolderText.optional)
        }
    }, [reason, otherReason, placeHolderText])

    // Reset form state when modal closes
    useEffect(() => {
      if (!open) {
        setReason(undefined);
        setNotes('');
        setDisableSubmit(true);
      }
    }, [open]);

    return <Dialog
            open={open}
            fullWidth
            >
            <DialogTitle>Decline Booking {booking?.orderNumber}</DialogTitle>
            <DialogContent>
                <FormGroup>
                    <DeclineBookingReasonSelect
                        selectedReason={reason}
                        declineReasons={declineBookingReasons}
                        handleSelectReason={handleSelectReason}
                    />
                    <SupplierBookingDisableModalContent>
                        <TextField
                            value={notes}
                            onChange={handleChangeNotes}
                            placeholder={notesPlaceholder}
                            required={reason === otherReason}
                            fullWidth
                        />
                    </SupplierBookingDisableModalContent>
                </FormGroup>
            </DialogContent>
            <SupplierBookingDisableModalButtons>
                <Button onClick={handleSubmit} disabled={disableSubmit}>Decline Booking</Button>
                <Button onClick={handleClose}>Cancel</Button>
            </SupplierBookingDisableModalButtons>
        </Dialog>
}

export default SupplierDeclineBookingModal;
